import { toggleComments } from '#react/app/document/comments/actions'
import { saveNamedDocumentVersion } from '#react/app/document/version-history/actions'
import { requestRenameSelection } from '#react/app/editor/selection/rename-dialog'
import { openSettingsDialog } from '#react/app/settings/dialog'
import { isEditing } from '#react/app/shell/keyboard/focus'
import { bindSpaceHandTool } from '#react/app/shell/keyboard/space-tool'
import type {
  KeyboardShortcutOptions,
  KeyboardShortcutRunOptions
} from '#react/app/shell/keyboard/types'
import { saveFigFile, saveFigFileAs } from '#react/app/shell/menu/files'
import { appMenuTinykeysShortcut } from '#react/app/shell/menu/shortcut'
import { getWorkspaceMode } from '#react/app/shell/workspace-mode'
import { editorCommandMetadata, type EditorCommandId } from '#react/editor/commands'
import { tinykeys } from 'tinykeys'
import type { KeyBindingMap } from 'tinykeys'

import { TOOL_SHORTCUTS, type Tool } from '@open-pencil/core/editor'

const VIEW_SAFE_TOOLS = new Set<Tool>(['SELECT', 'HAND'])

type ShortcutAction = (options: KeyboardShortcutRunOptions) => void

type ShortcutDefinition = {
  id: string
  keys: string | string[]
  run: ShortcutAction
  shouldPreventDefault?: (event: KeyboardEvent) => boolean
  global?: boolean
}

function commandShortcut(
  command: EditorCommandId,
  keys = editorCommandMetadata(command).keybinding
): ShortcutDefinition | null {
  return keys ? { id: command, keys, run: ({ runCommand }) => runCommand(command) } : null
}

function commandShortcuts(...commands: EditorCommandId[]): ShortcutDefinition[] {
  return commands.flatMap((command) => {
    const shortcut = commandShortcut(command)
    return shortcut ? [shortcut] : []
  })
}

function opacityBindings(): ShortcutDefinition[] {
  return ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => ({
    id: `selection-opacity-${digit}`,
    keys: digit,
    run: ({ keyEvent, actions }) => {
      if (keyEvent.metaKey || keyEvent.ctrlKey || keyEvent.altKey || keyEvent.shiftKey) return
      actions.opacityDigit(digit)
    },
    shouldPreventDefault: (event) =>
      !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey
  }))
}

const EDITOR_SHORTCUT_OVERLAY_SELECTOR =
  '[data-picker-content], [role="dialog"], [role="listbox"], [role="menu"]'

function originatedInOverlay(event: KeyboardEvent) {
  return event
    .composedPath()
    .some((target) => target instanceof Element && target.matches(EDITOR_SHORTCUT_OVERLAY_SELECTOR))
}

function hasOpenDismissableLayer() {
  return document.querySelector('[data-dismissable-layer]') !== null
}

function shouldIgnoreShortcut(event: KeyboardEvent, options: KeyboardShortcutOptions) {
  return (
    options.store.state.pageLoading.visible ||
    options.store.state.loading ||
    hasOpenDismissableLayer() ||
    originatedInOverlay(event) ||
    isEditing(event) ||
    options.inputFocused() ||
    !!options.store.state.editingTextId ||
    !!options.store.state.numberFieldFocused
  )
}

function bindShortcut(
  bindings: KeyBindingMap,
  keys: string | string[],
  run: (event: KeyboardEvent) => void
) {
  for (const key of Array.isArray(keys) ? keys : [keys]) bindings[key] = run
}

function bindToolShortcuts(bindings: KeyBindingMap, options: KeyboardShortcutRunOptions) {
  for (const [code, tool] of Object.entries(TOOL_SHORTCUTS)) {
    if (!tool) continue
    bindings[code] = (event: KeyboardEvent) => {
      const mode = getWorkspaceMode()
      if (mode !== 'edit' && !VIEW_SAFE_TOOLS.has(tool)) return
      event.preventDefault()
      options.spaceTool.resetToolBeforeSpace()
      options.store.setTool(tool)
    }
  }
}

export function registerKeyboardShortcuts(options: KeyboardShortcutOptions) {
  const spaceTool = bindSpaceHandTool(options.inputFocused, options.store)
  const runOptions = (event: KeyboardEvent): KeyboardShortcutRunOptions => ({
    ...options,
    keyEvent: event,
    spaceTool
  })

  const shortcuts: ShortcutDefinition[] = [
    ...commandShortcuts(
      'selection.createComponent',
      'selection.detachInstance',
      'selection.createComponentSet',
      'selection.toggleMask',
      'selection.toggleVisibility',
      'selection.toggleLock',
      'selection.flipHorizontal',
      'selection.flipVertical'
    ),
    {
      id: 'export-selection-png',
      keys: appMenuTinykeysShortcut('export-selection') ?? '$mod+Shift+KeyE',
      run: ({ actions }) => actions.exportSelectionPNG()
    },
    {
      id: 'save-as',
      keys: appMenuTinykeysShortcut('save-as') ?? '$mod+Shift+KeyS',
      run: ({ store }) => void saveFigFileAs(store)
    },
    ...commandShortcuts('selection.ungroup', 'edit.redo'),
    {
      id: 'toggle-ui',
      keys: appMenuTinykeysShortcut('toggle-ui') ?? '$mod+Backslash',
      run: ({ actions }) => actions.toggleUI()
    },
    { id: 'toggle-ai', keys: '$mod+KeyJ', run: ({ actions }) => actions.toggleAI() },
    {
      id: 'open-settings',
      keys: appMenuTinykeysShortcut('settings') ?? '$mod+Comma',
      run: () => openSettingsDialog(),
      global: true
    },
    {
      id: 'close-tab',
      keys: appMenuTinykeysShortcut('close') ?? '$mod+KeyW',
      run: ({ closeActiveTab }) => closeActiveTab()
    },
    {
      id: 'import-fig',
      keys: appMenuTinykeysShortcut('import') ?? '$mod+KeyO',
      run: ({ importFigDialog }) => importFigDialog()
    },
    {
      id: 'new-tab',
      keys: ['$mod+KeyT'],
      run: ({ createTab }) => createTab()
    },
    {
      id: 'rename-selection',
      keys: appMenuTinykeysShortcut('selection.rename') ?? '$mod+KeyR',
      run: ({ store }) => requestRenameSelection(store)
    },
    ...commandShortcuts(
      'edit.undo',
      'view.zoom100',
      'view.zoomFit',
      'view.zoomSelection',
      'selection.duplicate',
      'selection.selectAll',
      'selection.selectInverse'
    ),
    {
      id: 'save',
      keys: appMenuTinykeysShortcut('save') ?? '$mod+KeyS',
      run: ({ store }) => void saveFigFile(store)
    },
    {
      id: 'save-version',
      keys: appMenuTinykeysShortcut('save-version') ?? '$mod+Alt+KeyS',
      run: () => saveNamedDocumentVersion()
    },
    {
      id: 'show-comments',
      keys: appMenuTinykeysShortcut('show-comments') ?? 'Shift+KeyC',
      run: () => toggleComments()
    },
    ...commandShortcuts('selection.group'),
    {
      id: 'toggle-auto-layout',
      keys: 'Shift+KeyA',
      run: ({ actions }) => actions.toggleAutoLayout()
    },
    ...commandShortcuts(
      'selection.bringForward',
      'selection.bringToFront',
      'selection.sendBackward',
      'selection.sendToBack'
    ),
    { id: 'delete-backspace', keys: 'Backspace', run: ({ actions }) => actions.smartDelete(false) },
    { id: 'delete', keys: 'Delete', run: ({ actions }) => actions.smartDelete(false) },
    { id: 'delete-alt', keys: 'Alt+Delete', run: ({ actions }) => actions.smartDelete(true) },
    { id: 'enter', keys: 'Enter', run: ({ actions }) => actions.confirmOrEnterText() },
    { id: 'escape', keys: 'Escape', run: ({ actions }) => actions.escapeOrDeselect() },
    ...opacityBindings()
  ]

  const bindings: KeyBindingMap = {}
  const globalBindings: KeyBindingMap = {}
  bindToolShortcuts(bindings, runOptions(new KeyboardEvent('keydown')))

  for (const shortcut of shortcuts) {
    bindShortcut(shortcut.global ? globalBindings : bindings, shortcut.keys, (event) => {
      shortcut.run(runOptions(event))
      if (shortcut.shouldPreventDefault?.(event) ?? true) event.preventDefault()
    })
  }

  const unsubscribeEditor = tinykeys(
    window,
    Object.fromEntries(
      Object.entries(bindings).map(([keys, handler]) => [
        keys,
        (event: KeyboardEvent) => {
          if (shouldIgnoreShortcut(event, options)) return
          handler(event)
        }
      ])
    ),
    { capture: true }
  )

  const unsubscribeGlobal = tinykeys(window, globalBindings, { capture: true })

  return () => {
    unsubscribeEditor()
    unsubscribeGlobal()
    spaceTool.dispose()
  }
}
